import React, { useState, useEffect, useContext } from 'react';
import axios from '../api/axios';
import { AuthContext } from '../context/AuthContext';
import { FiDollarSign, FiCreditCard, FiCopy, FiRefreshCw, FiPlus } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateWallet, setShowCreateWallet] = useState(false);
  const [selectedCrypto, setSelectedCrypto] = useState('BTC');
  const [creating, setCreating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { isAuthenticated, user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadOrders();
    loadWallets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/orders');
      setOrders(res.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const loadWallets = async () => {
    try {
      const res = await axios.get('/api/wallet/balances');
      setWallets(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Address copied to clipboard!');
  };

  const handleCreateWallet = async () => {
    try {
      setCreating(true);
      await axios.post('/api/wallet/create', { cryptocurrency: selectedCrypto });
      setShowCreateWallet(false);
      await loadWallets();
      alert('Wallet created successfully!');
    } catch (err) {
      alert(err.response?.data?.msg || 'Failed to create wallet');
    }
    setCreating(false);
  };

  const getAvailableCryptos = () => {
    const existing = wallets.map(w => w.cryptocurrency);
    return ['BTC', 'ETC', 'LTC', 'DOGE'].filter(c => !existing.includes(c));
  };

  const refreshBalances = async () => {
    await loadWallets();
  };

  const handleDeleteAccount = async () => {
    try {
      setDeleting(true);
      await axios.delete('/api/auth/account');
      alert('Account successfully deleted');
      logout();
      navigate('/');
    } catch (err) {
      alert(err.response?.data?.msg || 'Failed to delete account');
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Dashboard</h1>
        <p className="text-gray-600">Welcome back, {user?.name}!</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('orders')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'orders'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FiCreditCard className="inline mr-2" />
            My Gift Cards
          </button>
          <button
            onClick={() => setActiveTab('wallets')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'wallets'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FiDollarSign className="inline mr-2" />
            My Wallets
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'settings'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Settings
          </button>
        </nav>
      </div>

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <div>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : orders.length > 0 ? (
            <div className="space-y-6">
              {orders.map((order) => (
                <div key={order._id} className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold">Order #{order._id.slice(-8)}</h3>
                      <p className="text-sm text-gray-600">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        order.status === 'fulfilled'
                          ? 'bg-green-100 text-green-800'
                          : order.status === 'pending_fulfillment'
                          ? 'bg-yellow-100 text-yellow-800'
                          : order.status === 'rejected'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {order.status.replace('_', ' ')}
                    </span>
                  </div>

                  <div className="space-y-4">
                    {order.items.map((item, index) => (
                      <div key={index} className="border-t pt-4">
                        <div className="flex items-start">
                          {item.giftCard?.image && (
                            <img
                              src={item.giftCard.image}
                              alt={item.giftCard?.name}
                              className="w-16 h-16 object-cover rounded mr-4"
                            />
                          )}
                          <div className="flex-1">
                            <h4 className="font-semibold">{item.giftCard?.name}</h4>
                            <p className="text-sm text-gray-600">
                              ${item.denomination} × {item.quantity}
                            </p>
                          </div>
                        </div>

                        {order.status === 'fulfilled' && item.code && (
                          <div className="mt-4 bg-green-50 p-3 rounded">
                            <p className="text-sm font-medium mb-1">Gift Card Code:</p>
                            <p className="font-mono text-sm bg-white p-2 rounded border">{item.code}</p>
                            {item.pin && (
                              <>
                                <p className="text-sm font-medium mb-1 mt-2">PIN:</p>
                                <p className="font-mono text-sm bg-white p-2 rounded border">{item.pin}</p>
                              </>
                            )}
                          </div>
                        )}
                        {order.status === 'pending_fulfillment' && (
                          <div className="mt-4 bg-yellow-50 p-3 rounded">
                            <p className="text-sm text-yellow-700">
                              ⏳ Order received. Awaiting fulfillment from admin.
                            </p>
                          </div>
                        )}
                        {order.status === 'rejected' && order.adminNotes && (
                          <div className="mt-4 bg-red-50 p-3 rounded">
                            <p className="text-sm text-red-700">
                              ❌ Order rejected: {order.adminNotes}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 pt-4 border-t flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600">Payment Method:</p>
                      <p className="font-semibold">{order.cryptocurrency}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Total:</p>
                      <p className="text-lg font-bold">${order.totalUSD.toFixed(2)}</p>
                      <p className="text-sm text-gray-600">
                        {order.cryptoAmount.toFixed(8)} {order.cryptocurrency}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <FiCreditCard className="mx-auto mb-4" size={48} />
              <p className="text-xl">No orders yet</p>
              <button
                onClick={() => navigate('/catalog')}
                className="mt-4 bg-primary text-white px-6 py-2 rounded-md hover:bg-blue-600"
              >
                Start Shopping
              </button>
            </div>
          )}
        </div>
      )}

      {/* Wallets Tab */}
      {activeTab === 'wallets' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold">My Platform Wallets</h2>
            <div className="flex gap-2">
              <button
                onClick={refreshBalances}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 inline-flex items-center"
              >
                <FiRefreshCw className="mr-2" />
                Refresh Balances
              </button>
              {getAvailableCryptos().length > 0 && (
                <button
                  onClick={() => setShowCreateWallet(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 inline-flex items-center"
                >
                  <FiPlus className="mr-2" />
                  Create Wallet
                </button>
              )}
            </div>
          </div>

          {showCreateWallet && (
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">Create New Wallet</h3>
              <p className="text-sm text-gray-600 mb-4">
                Create a new platform-managed wallet using BlockCypher. You'll receive a unique deposit address for this cryptocurrency.
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Cryptocurrency
                </label>
                <select
                  value={selectedCrypto}
                  onChange={(e) => setSelectedCrypto(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {getAvailableCryptos().map(crypto => (
                    <option key={crypto} value={crypto}>
                      {crypto === 'BTC' && 'Bitcoin (BTC)'}
                      {crypto === 'ETC' && 'Ethereum Classic (ETC)'}
                      {crypto === 'LTC' && 'Litecoin (LTC)'}
                      {crypto === 'DOGE' && 'Dogecoin (DOGE)'}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCreateWallet}
                  disabled={creating}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {creating ? 'Creating...' : 'Create Wallet'}
                </button>
                <button
                  onClick={() => setShowCreateWallet(false)}
                  className="bg-gray-200 text-gray-800 px-6 py-2 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
            <p className="text-sm text-blue-700">
              <strong>How it works:</strong> These are your platform-managed wallets. Send cryptocurrency to the deposit addresses below to add funds to your balance. Once received and confirmed by an admin, you can use your balance to purchase gift cards.
            </p>
          </div>

          {wallets.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-6">
              {wallets.map((wallet, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-semibold mb-1">{wallet.cryptocurrency}</h3>
                      <p className="text-sm text-gray-600">
                        {wallet.cryptocurrency === 'BTC' && 'Bitcoin'}
                        {wallet.cryptocurrency === 'ETC' && 'Ethereum Classic'}
                        {wallet.cryptocurrency === 'LTC' && 'Litecoin'}
                        {wallet.cryptocurrency === 'DOGE' && 'Dogecoin'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-primary">
                        {wallet.balance.toFixed(8)}
                      </p>
                      <p className="text-sm text-gray-600">
                        ${wallet.balanceUSD.toFixed(2)} USD
                      </p>
                    </div>
                  </div>
                  
                  <div className="mb-4 bg-gray-50 p-4 rounded">
                    <p className="text-sm text-gray-600 mb-2 font-medium">Deposit Address:</p>
                    <div className="flex items-center gap-2">
                      <p className="font-mono text-xs bg-white p-2 rounded border flex-1 break-all">
                        {wallet.depositAddress}
                      </p>
                      <button
                        onClick={() => copyToClipboard(wallet.depositAddress)}
                        className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600 flex-shrink-0"
                        title="Copy address"
                      >
                        <FiCopy />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Send {wallet.cryptocurrency} to this address to add funds
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <FiDollarSign className="mx-auto mb-4" size={48} />
              <p className="text-xl">Loading wallets...</p>
            </div>
          )}
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div>
          <div className="max-w-3xl">
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-2xl font-semibold mb-4">Account Information</h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600">Name</p>
                  <p className="font-medium">{user?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-medium">{user?.email}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 border-2 border-red-200">
              <h2 className="text-2xl font-semibold mb-4 text-red-600">Danger Zone</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Delete Account</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Permanently delete your account and all associated data. This action cannot be undone.
                  </p>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700"
                  >
                    Delete Account
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                <h3 className="text-xl font-semibold mb-4 text-red-600">Confirm Account Deletion</h3>
                <p className="text-gray-700 mb-6">
                  Are you sure you want to delete your account? This will permanently delete:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-6 space-y-1">
                  <li>Your profile information</li>
                  <li>All your orders and gift cards</li>
                  <li>Your wallet balances and transaction history</li>
                  <li>All other account data</li>
                </ul>
                <p className="text-red-600 font-semibold mb-6">
                  This action cannot be undone!
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deleting}
                    className="flex-1 bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 disabled:bg-gray-400"
                  >
                    {deleting ? 'Deleting...' : 'Yes, Delete My Account'}
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={deleting}
                    className="flex-1 bg-gray-200 text-gray-800 px-6 py-2 rounded-md hover:bg-gray-300 disabled:bg-gray-100"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
